import { useEffect, useMemo, useRef, useState } from 'react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import QrScanner from 'qr-scanner';

const money = (cents) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: cents % 100 === 0 ? 0 : 2
}).format(cents / 100);

async function api(path, options = {}) {
  const response = await fetch(path, {
    ...options,
    headers: options.body ? { 'Content-Type': 'application/json', ...(options.headers || {}) } : options.headers
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(result.error || 'Something went wrong. Please try again.');
    error.status = response.status;
    error.details = result;
    throw error;
  }
  return result;
}

function Logo({ compact = false }) {
  return (
    <a className={`brand ${compact ? 'brand-compact' : ''}`} href="/" aria-label="Ulster Scottish Pipe Band celebration home">
      <span className="brand-mark" aria-hidden="true"><img src="/media/ulster-logo.jpg" alt="" /></span>
      <span className="brand-name"><strong>Ulster Scottish</strong><span>Pipe Band</span></span>
    </a>
  );
}

function LoadingPage() {
  return <main className="loading-page"><div className="loading-mark" /><p>Preparing the celebration…</p></main>;
}

function Quantity({ value, max, onChange }) {
  return (
    <div className="quantity" aria-label="Ticket quantity">
      <button type="button" onClick={() => onChange(Math.max(1, value - 1))} disabled={value <= 1} aria-label="Remove one ticket">−</button>
      <output aria-live="polite">{value}</output>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label="Add one ticket">+</button>
    </div>
  );
}

function Checkout({ config }) {
  const [quantity, setQuantity] = useState(1);
  const [buyer, setBuyer] = useState({ purchaserName: '', purchaserEmail: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [processing, setProcessing] = useState(false);
  const checkoutRef = useRef(null);
  const max = Math.max(1, config.tickets.maxPerOrder || 1);
  const total = config.tickets.priceCents * quantity;
  const valid = buyer.purchaserName.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(buyer.purchaserEmail);

  const createCheckout = async () => {
    setError('');
    const result = await api('/api/orders/create', {
      method: 'POST',
      body: JSON.stringify({ ...buyer, quantity })
    });
    checkoutRef.current = result;
    return result;
  };

  const capture = async (orderId, paymentDetails = {}) => {
    setBusy(true);
    setError('');
    try {
      const result = await api(`/api/orders/${encodeURIComponent(orderId)}/capture`, {
        method: 'POST',
        body: JSON.stringify(paymentDetails)
      });
      window.location.assign(`/order/${result.order.receiptToken}`);
    } catch (captureError) {
      setError(captureError.message);
      setBusy(false);
      setProcessing(false);
    }
  };

  const completeMock = async () => {
    setBusy(true);
    setProcessing(true);
    try {
      const checkout = await createCheckout();
      await capture(checkout.orderId);
    } catch (mockError) {
      setError(mockError.message);
      setBusy(false);
      setProcessing(false);
    }
  };

  const announced = config.tickets.priceCents > 0;
  return (
    <aside className="checkout-card" id="tickets" aria-labelledby="checkout-title">
      <div className="ticket-stripe"><i /><i /><i /><i /></div>
      <p className="eyebrow eyebrow-dark">{config.event.title}</p>
      <h2 id="checkout-title">Save your place.</h2>
      <div className="checkout-event">
        <time dateTime="2026-10-10T19:00:00-04:00"><strong>10</strong><span>OCT</span></time>
        <p><strong>Saturday · October 10</strong><br />Parade at {config.event.paradeTime} · Doors at {config.event.doorsTime}<br />{config.event.venue}<br /><span>{config.event.address}</span></p>
      </div>

      <div className="checkout-row">
        <div><span className="field-label">Celebration admission</span><strong className="price">{announced ? money(config.tickets.priceCents) : 'Price announced soon'}</strong></div>
        <Quantity value={quantity} max={max} onChange={setQuantity} />
      </div>

      <div className="buyer-fields">
        <label><span>Purchaser name</span><input autoComplete="name" value={buyer.purchaserName} onChange={(event) => setBuyer({ ...buyer, purchaserName: event.target.value })} placeholder="Full name" /></label>
        <label><span>Email for tickets</span><input type="email" autoComplete="email" value={buyer.purchaserEmail} onChange={(event) => setBuyer({ ...buyer, purchaserEmail: event.target.value })} placeholder="you@example.com" /></label>
      </div>

      {announced && <div className="total-line"><span>Total</span><strong>{money(total)}</strong></div>}
      {!config.tickets.salesOpen && <div className="sales-note">Online checkout is opening soon. Tickets are {money(config.tickets.priceCents)} per person.</div>}
      {config.tickets.salesOpen && !valid && <p className="payment-hint">Enter your name and email to enable checkout.</p>}
      {config.tickets.salesOpen && config.payments.mock && (
        <button className="purchase-button" type="button" disabled={!valid || busy} onClick={completeMock}>{busy ? 'Creating tickets…' : 'Complete demo purchase'}</button>
      )}
      {config.tickets.salesOpen && !config.payments.mock && config.payments.configured && (
        <div className={`paypal-wrap ${!valid || busy ? 'paypal-disabled' : ''}`} aria-disabled={!valid || busy}>
          <PayPalButtons
            style={{ layout: 'vertical', shape: 'rect', label: 'paypal', height: 48 }}
            disabled={!valid || busy}
            forceReRender={[quantity, buyer.purchaserName, buyer.purchaserEmail]}
            createOrder={async (_data, actions) => {
              try {
                const checkout = await createCheckout();
                if (!checkout.clientCapture) return checkout.orderId;
                return actions.order.create({
                  purchase_units: [{
                    reference_id: checkout.reference,
                    description: 'Ulster Scottish Worlds Champions Celebration tickets',
                    amount: { currency_code: 'USD', value: checkout.amount }
                  }],
                  application_context: {
                    brand_name: 'Ulster Scottish Pipe Band',
                    shipping_preference: 'NO_SHIPPING',
                    user_action: 'PAY_NOW'
                  }
                });
              } catch (checkoutError) {
                setProcessing(false);
                throw checkoutError;
              }
            }}
            onApprove={async ({ orderID }, actions) => {
              setError('');
              setBusy(true);
              setProcessing(true);
              try {
                const checkout = checkoutRef.current;
                if (checkout?.clientCapture) {
                  const paypalCapture = await actions.order.capture();
                  await capture(orderID, { checkoutSessionId: checkout.orderId, paypalCapture });
                } else {
                  await capture(orderID);
                }
              } catch {
                setProcessing(false);
                setBusy(false);
                setError('PayPal could not complete checkout. Please try again.');
              }
            }}
            onCancel={() => {
              setProcessing(false);
              setBusy(false);
              checkoutRef.current = null;
              setError('Checkout was canceled. No payment was taken.');
            }}
            onError={() => {
              setProcessing(false);
              setBusy(false);
              checkoutRef.current = null;
              setError('PayPal could not complete checkout. Please try again.');
            }}
          />
        </div>
      )}
      {error && <p className="form-error" role="alert">{error}</p>}
      <p className="secure-note"><span aria-hidden="true">✓</span> Secure PayPal checkout · One QR ticket per guest</p>
      {processing && (
        <div className="checkout-processing" role="status" aria-live="assertive" aria-label="Processing payment">
          <span className="processing-spinner" aria-hidden="true" />
          <strong>Processing your payment…</strong>
          <small>Keep this page open while we create and email your tickets.</small>
        </div>
      )}
    </aside>
  );
}

function OptionOneLanding({ config }) {
  const content = (
    <div className="site-shell">
      <header className="site-header">
        <div className="header-inner">
          <Logo />
          <p>2026 Grade 2 World Champions</p>
          <a href="#tickets">Tickets · $25</a>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">Ulster Scottish Pipe Band · 2026</p>
            <h1><span>World</span><em>champions.</em></h1>
            <p className="hero-intro">The Grade 2 title is coming home. Let’s celebrate it properly.</p>
            <a className="hero-link" href="#tickets">Join the party <span aria-hidden="true">↓</span></a>
          </div>
          <div className="hero-visual">
            <div className="hero-photo" role="img" aria-label="Ulster Scottish Pipe Band celebrating its 2026 Grade 2 World Championship" />
          </div>
          <div className="hero-event-strip">
            <div><span>Date</span><strong>October 10, 2026</strong></div>
            <div><span>Schedule</span><strong>Parade 7:00 · Doors 7:30</strong></div>
            <div><span>Place</span><strong>North Penn VFW</strong></div>
            <a href="#tickets"><span>Admission</span><strong>$25 per person →</strong></a>
          </div>
        </section>

        <section className="ticket-section">
          <div className="event-copy">
            <p className="eyebrow eyebrow-dark">Worlds celebration · October 10</p>
            <h2>Come raise<br />a glass.</h2>
            <p>Ulster Scottish won the Grade 2 final and the Grade 2 drumming prize on the world’s biggest stage. This is one night for the band, our families, our friends, and everyone who helped make it possible.</p>
            <dl className="event-facts">
              <div><dt>Date</dt><dd>Saturday, October 10, 2026<br /><small>Parade 7:00 PM · Doors 7:30 PM</small></dd></div>
              <div><dt>Venue</dt><dd>North Penn VFW<br /><small>2519 Jenkintown Road · Glenside, PA 19038</small></dd></div>
              <div><dt>Admission</dt><dd>$25 per person<br /><small>Every guest receives a QR ticket</small></dd></div>
            </dl>
          </div>
          <Checkout config={config} />
        </section>

        <section className="championship-band" aria-label="Celebration details">
          <div><strong>World champions</strong><p>Celebrating the 2026 Grade 2 title.</p></div>
          <div><strong>One night together</strong><p>Band, families, friends, and supporters.</p></div>
          <div><strong>North Penn VFW</strong><p>Parade at 7:00 · Doors at 7:30.</p></div>
        </section>
      </main>

      <footer><Logo compact /><p>2026 Grade 2 World Champions</p><a href="mailto:ulsterpb@gmail.com">ulsterpb@gmail.com</a></footer>
    </div>
  );

  if (config.payments.configured && !config.payments.mock) {
    return <PayPalScriptProvider options={{ clientId: config.payments.paypalClientId, currency: 'USD', intent: 'capture', components: 'buttons' }}>{content}</PayPalScriptProvider>;
  }
  return content;
}

function OptionTwoLanding({ config }) {
  const content = (
    <div className="site-shell option-two">
      <header className="o2-header">
        <Logo />
        <p>Ulster Scottish · World Champions · 2026</p>
        <a href="#tickets">Get tickets · $25</a>
      </header>

      <main>
        <section className="o2-hero">
          <div className="o2-copy">
            <p className="o2-kicker">Worlds Champions Celebration</p>
            <h1><span>They did it.</span><em>Now we party.</em></h1>
            <p>One world title. One world-champion drum corps. One night to celebrate everyone who helped bring it home.</p>
            <div className="o2-actions">
              <a href="#tickets">Get $25 tickets</a>
              <a href="https://maps.google.com/?q=2519+Jenkintown+Road+Glenside+PA+19038" target="_blank" rel="noreferrer">North Penn VFW ↗</a>
            </div>
          </div>
          <figure className="o2-photo">
            <img src="/media/band-worlds-2026.jpg" alt="Ulster Scottish Pipe Band gathered with its drums and trophies in Glasgow" />
            <figcaption>Ulster Scottish Pipe Band · Glasgow 2026</figcaption>
            <div className="o2-date"><strong>10</strong><span>OCT<br />2026</span></div>
          </figure>
        </section>

        <div className="o2-ticker" aria-label="Event summary">
          <span>Grade 2 World Champions</span><i />
          <span>Parade 7 PM · Doors 7:30 PM</span><i />
          <span>North Penn VFW · Glenside</span><i />
          <span>$25 Tickets</span>
        </div>

        <section className="o2-ticket-section">
          <div className="o2-ticket-intro">
            <p className="o2-kicker">You’re invited</p>
            <span className="o2-price">$25</span>
            <h2>One unforgettable night.</h2>
            <p>Saturday, October 10<br />Parade 7:00 PM · Doors 7:30 PM<br />North Penn VFW · Glenside</p>
            <small>Each guest receives a unique QR ticket for admission.</small>
          </div>
          <Checkout config={config} />
        </section>

        <section className="o2-finale">
          <p className="o2-kicker">Grade 2 · Glasgow 2026</p>
          <h2>Glasgow was the win.<br />Glenside is the celebration.</h2>
          <a href="#tickets">Be there October 10 →</a>
        </section>
      </main>

      <footer className="o2-footer"><Logo compact /><p>2026 Grade 2 World Champions</p><a href="mailto:ulsterpb@gmail.com">ulsterpb@gmail.com</a></footer>
    </div>
  );

  if (config.payments.configured && !config.payments.mock) {
    return <PayPalScriptProvider options={{ clientId: config.payments.paypalClientId, currency: 'USD', intent: 'capture', components: 'buttons' }}>{content}</PayPalScriptProvider>;
  }
  return content;
}

function OptionThreeLanding({ config }) {
  const content = (
    <div className="site-shell option-three">
      <header className="o3-header">
        <div className="o3-header-inner">
          <Logo />
          <p>Parade 7:00 PM · Doors 7:30 PM</p>
          <a href="#tickets">Tickets · $25</a>
        </div>
      </header>

      <main>
        <section className="o3-intro">
          <p className="o3-kicker">Ulster Scottish Pipe Band · Glasgow 2026</p>
          <h1>Worlds Champions<br />Celebration</h1>
          <p className="o3-lead">Join the band, our families, and our friends for an evening celebrating the 2026 Grade 2 World Championship.</p>
          <a className="o3-primary-link" href="#tickets">Reserve tickets <span>→</span></a>
        </section>

        <figure className="o3-image">
          <img src="/media/band-worlds-2026.jpg" alt="Ulster Scottish Pipe Band gathered with its drums and trophies in Glasgow" />
          <figcaption>
            <span>Ulster Scottish Pipe Band</span>
            <span>2026 Grade 2 World Champions · World Champion Drum Corps</span>
          </figcaption>
        </figure>

        <section className="o3-details" aria-label="Party details">
          <div><span>When</span><strong>Saturday, October 10, 2026</strong><small>Parade 7:00 PM · Doors 7:30 PM</small></div>
          <div><span>Where</span><strong>North Penn VFW</strong><small>2519 Jenkintown Road, Glenside, PA 19038</small></div>
          <div><span>Admission</span><strong>$25 per person</strong><small>One QR ticket for each guest</small></div>
        </section>

        <section className="o3-ticket-section">
          <div className="o3-ticket-copy">
            <p className="o3-kicker">Saturday, October 10</p>
            <h2>Be there<br />to celebrate.</h2>
            <p>Choose the number of guests, enter your contact information, and pay securely with PayPal. Your individual QR tickets will arrive by email.</p>
            <a href="https://maps.google.com/?q=2519+Jenkintown+Road+Glenside+PA+19038" target="_blank" rel="noreferrer">View the venue on Google Maps ↗</a>
          </div>
          <Checkout config={config} />
        </section>
      </main>

      <footer className="o3-footer"><Logo compact /><p>Worlds Champions Celebration · 2026</p><a href="mailto:ulsterpb@gmail.com">ulsterpb@gmail.com</a></footer>
    </div>
  );

  if (config.payments.configured && !config.payments.mock) {
    return <PayPalScriptProvider options={{ clientId: config.payments.paypalClientId, currency: 'USD', intent: 'capture', components: 'buttons' }}>{content}</PayPalScriptProvider>;
  }
  return content;
}

function LandingPage({ config }) {
  const content = (
    <div className="site-shell option-four">
      <header className="o4-header">
        <div className="o4-header-inner">
          <Logo />
          <p>2026 Grade 2 World Champions</p>
          <a href="#tickets">Tickets · $25</a>
        </div>
      </header>

      <main>
        <section className="o4-hero">
          <div className="o4-invitation">
            <div className="o4-copy">
              <p className="o4-kicker">Ulster Scottish Pipe Band · 2026 Grade 2</p>
              <h1><span>Worlds Champions</span><strong>Celebration</strong></h1>
              <p>Come celebrate a world championship season with the band, our families, friends, and supporters.</p>
              <a href="#tickets">Reserve $25 tickets <span aria-hidden="true">→</span></a>
            </div>
            <figure className="o4-photo">
              <img src="/media/band-worlds-2026.jpg" alt="Ulster Scottish Pipe Band gathered with its drums and trophies in Glasgow" />
            </figure>
          </div>

          <div className="o4-event-bar" aria-label="Celebration details">
            <div className="o4-date"><strong>10</strong><span>October<br />2026</span></div>
            <div><span>Parade</span><strong>7:00 PM</strong></div>
            <div><span>Doors open</span><strong>7:30 PM</strong></div>
            <div className="o4-place"><span>North Penn VFW</span><strong>2519 Jenkintown Road<br />Glenside, PA 19038</strong></div>
            <a href="#tickets"><span>Admission</span><strong>$25 per person →</strong></a>
          </div>
        </section>

        <section className="o4-ticket-section">
          <div className="o4-ticket-copy">
            <p className="o4-kicker">Saturday, October 10</p>
            <h2>Join the<br />celebration.</h2>
            <p>The parade begins at 7:00 PM, followed by doors opening at 7:30 PM at North Penn VFW. Each guest receives an individual QR ticket for admission.</p>
            <a href="https://maps.google.com/?q=2519+Jenkintown+Road+Glenside+PA+19038" target="_blank" rel="noreferrer">Get directions to North Penn VFW ↗</a>
          </div>
          <Checkout config={config} />
        </section>
      </main>

      <footer className="o4-footer"><Logo compact /><p>2026 Grade 2 · Worlds Champions Celebration</p><a href="mailto:ulsterpb@gmail.com">ulsterpb@gmail.com</a></footer>
    </div>
  );

  if (config.payments.configured && !config.payments.mock) {
    return <PayPalScriptProvider options={{ clientId: config.payments.paypalClientId, currency: 'USD', intent: 'capture', components: 'buttons' }}>{content}</PayPalScriptProvider>;
  }
  return content;
}

function TicketCard({ ticket, order, event, standalone = false }) {
  return (
    <article className={`digital-ticket ${ticket.checkedInAt ? 'ticket-used' : ''}`}>
      <div className="ticket-topline"><span>Ulster Scottish</span><span>{standalone ? 'Admit one' : `Admission ${order.tickets.indexOf(ticket) + 1} of ${order.quantity}`}</span></div>
      <div className="ticket-main">
        <img src={`/api/tickets/${encodeURIComponent(ticket.token)}/qr.png`} alt={`QR code for ticket ${ticket.code}`} />
        <div><p className="eyebrow eyebrow-dark">{event.title}</p><h2>{event.dateShort}</h2><p>{event.time}<br />{event.venue}<br />{event.address}</p><strong className="ticket-code">{ticket.code}</strong></div>
      </div>
      <div className="ticket-status">{ticket.checkedInAt ? 'Already checked in' : 'Show this QR code at the door'}</div>
    </article>
  );
}

function ReceiptPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api(`/api/orders/receipt/${encodeURIComponent(token)}`).then(setData).catch((reason) => setError(reason.message)); }, [token]);
  if (!data && !error) return <LoadingPage />;
  if (error) return <NotFound message={error} />;
  const { order, event } = data;
  return (
    <main className="receipt-page">
      <div className="receipt-header"><Logo /><button type="button" onClick={() => window.print()}>Print tickets</button></div>
      <section className="receipt-intro"><p className="eyebrow">Payment complete</p><h1>You’re on the list.</h1><p>{order.quantity} QR ticket{order.quantity === 1 ? '' : 's'} for <strong>{order.purchaserEmail}</strong>. {order.emailStatus === 'sent' ? 'A copy is on its way by email.' : 'Save or print this page before arriving.'}</p><div><span>Order</span><strong>{order.id}</strong><span>Total</span><strong>{money(order.totalCents)}</strong></div></section>
      <section className="ticket-list">{order.tickets.map((ticket) => <TicketCard key={ticket.id} ticket={ticket} order={order} event={event} />)}</section>
      <p className="receipt-help">Each QR code admits one guest and can be scanned once. Questions? <a href="mailto:ulsterpb@gmail.com">ulsterpb@gmail.com</a></p>
    </main>
  );
}

function SingleTicketPage({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api(`/api/tickets/${encodeURIComponent(token)}`).then(setData).catch((reason) => setError(reason.message)); }, [token]);
  if (!data && !error) return <LoadingPage />;
  if (error) return <NotFound message={error} />;
  const ticket = { ...data, token };
  return <main className="single-ticket-page"><Logo /><TicketCard ticket={ticket} order={{ tickets: [ticket], quantity: 1 }} event={data.event} standalone /><p>Keep this page available for the door. A screenshot works too.</p></main>;
}

function CheckInResult({ result, onDismiss }) {
  if (!result) return null;
  const labels = { admitted: ['SUCCESS', 'Checked in. Welcome to the celebration.'], duplicate: ['ALREADY USED', 'This ticket has already been checked in.'], invalid: ['NOT FOUND', 'This is not a valid event ticket.'] };
  const [title, description] = labels[result.status] || labels.invalid;
  return (
    <div className={`scan-result scan-${result.status}`} role="status">
      <p>{title}</p><strong>{result.ticket?.code || 'Invalid ticket'}</strong><span>{result.ticket?.purchaserName || description}</span>{result.status === 'duplicate' && result.ticket?.checkedInAt && <small>Checked in {new Date(result.ticket.checkedInAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</small>}
      <button type="button" onClick={onDismiss}>Scan next ticket</button>
    </div>
  );
}

function AdminPage() {
  const [authenticated, setAuthenticated] = useState(null);
  const [pin, setPin] = useState('');
  const [loginError, setLoginError] = useState('');
  const [summary, setSummary] = useState(null);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [syncState, setSyncState] = useState('connecting');
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const busyRef = useRef(false);

  const refresh = async () => setSummary(await api('/api/admin/summary'));
  useEffect(() => { api('/api/admin/session').then(({ authenticated: value }) => { setAuthenticated(value); if (value) refresh(); }); }, []);

  useEffect(() => {
    if (!authenticated) return undefined;
    let disposed = false;
    let refreshing = false;
    const sync = async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        const next = await api('/api/admin/summary');
        if (!disposed) {
          setSummary(next);
          setSyncState('live');
        }
      } catch {
        if (!disposed) setSyncState('reconnecting');
      } finally {
        refreshing = false;
      }
    };
    const events = new EventSource('/api/admin/events');
    events.onopen = () => { if (!disposed) setSyncState('live'); };
    events.addEventListener('refresh', sync);
    events.onerror = () => { if (!disposed) setSyncState('reconnecting'); };
    const interval = window.setInterval(sync, 10_000);
    const syncWhenVisible = () => { if (document.visibilityState === 'visible') sync(); };
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', syncWhenVisible);
    return () => {
      disposed = true;
      events.close();
      window.clearInterval(interval);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', syncWhenVisible);
    };
  }, [authenticated]);

  const submitLogin = async (event) => {
    event.preventDefault();
    setLoginError('');
    try {
      await api('/api/admin/login', { method: 'POST', body: JSON.stringify({ pin }) });
      setAuthenticated(true);
      await refresh();
    } catch (error) { setLoginError(error.message); }
  };

  const submitTicket = async (value) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      await scannerRef.current?.pause();
      const response = await api('/api/admin/check-in', { method: 'POST', body: JSON.stringify({ ticket: value }) });
      setResult(response);
      setSummary((current) => current ? {
        ...current,
        counts: response.counts,
        tickets: current.tickets.map((ticket) => ticket.id === response.ticket.id
          ? { ...ticket, checked_in_at: response.ticket.checkedInAt }
          : ticket)
      } : current);
      navigator.vibrate?.(120);
    } catch (error) {
      const details = error.details || {};
      setResult({ status: details.status || 'invalid', ticket: details.ticket });
      navigator.vibrate?.([100, 70, 100]);
    }
  };

  const dismissResult = async () => {
    setResult(null);
    try {
      if (cameraOn) await scannerRef.current?.start();
    } catch (error) {
      setCameraError(error.message || 'Camera access was unavailable.');
      setCameraOn(false);
    } finally {
      busyRef.current = false;
    }
  };

  useEffect(() => {
    if (!cameraOn || !videoRef.current) return undefined;
    const scanner = new QrScanner(videoRef.current, ({ data }) => submitTicket(data), {
      returnDetailedScanResult: true,
      highlightScanRegion: true,
      highlightCodeOutline: true,
      maxScansPerSecond: 5
    });
    scannerRef.current = scanner;
    scanner.start().catch((error) => { setCameraError(error.message || 'Camera access was unavailable.'); setCameraOn(false); });
    return () => { scanner.stop(); scanner.destroy(); scannerRef.current = null; };
  }, [cameraOn]);

  if (authenticated == null) return <LoadingPage />;
  if (!authenticated) return (
    <main className="admin-login"><Logo /><form onSubmit={submitLogin}><p className="eyebrow eyebrow-dark">Event admin</p><h1>Check-in</h1><p>Enter the shared admin PIN to open the scanner.</p><label><span>Admin PIN</span><input type="password" inputMode="numeric" autoComplete="one-time-code" value={pin} onChange={(event) => setPin(event.target.value)} autoFocus /></label><button type="submit">Open scanner</button>{loginError && <p className="form-error" role="alert">{loginError}</p>}</form></main>
  );

  return (
    <main className="admin-page">
      <header><Logo compact /><div><span className={`live-sync live-sync-${syncState}`}><i />{syncState === 'live' ? 'Live sync' : 'Reconnecting'}</span><span><strong>{summary?.counts.checkedIn || 0}</strong> checked in</span><span><strong>{summary?.counts.sold || 0}</strong> sold</span><a href="/api/admin/export.csv">Export CSV</a></div></header>
      <section className="scanner-panel">
        <div className={`camera-shell ${cameraOn ? 'camera-live' : ''}`}>
          <video ref={videoRef} muted playsInline />
          {!cameraOn && <div><strong>Ready to scan</strong><p>Use this phone’s camera or enter a code below.</p><button type="button" onClick={() => { setCameraError(''); setCameraOn(true); }}>Start camera</button></div>}
        </div>
        {cameraError && <p className="form-error" role="alert">{cameraError}</p>}
        <form className="manual-checkin" onSubmit={(event) => { event.preventDefault(); submitTicket(manual); setManual(''); }}><label><span>Ticket code</span><input value={manual} onChange={(event) => setManual(event.target.value)} placeholder="ULS-XXXXX-XXXXX" autoCapitalize="characters" /></label><button type="submit" disabled={!manual.trim()}>Check in</button></form>
      </section>
      <section className="guest-list"><div><p className="eyebrow eyebrow-dark">Guest list</p><h2>Tonight’s tickets</h2><button type="button" onClick={refresh}>Refresh</button></div><div className="guest-table">{summary?.tickets.map((ticket) => <article key={ticket.id}><span className={ticket.checked_in_at ? 'status-in' : 'status-out'}>{ticket.checked_in_at ? 'In' : 'Out'}</span><p><strong>{ticket.purchaser_name}</strong><small>{ticket.code} · {ticket.purchaser_email}</small></p>{ticket.checked_in_at && <button type="button" onClick={async () => { await api(`/api/admin/tickets/${ticket.id}/undo`, { method: 'POST', body: '{}' }); await refresh(); }}>Undo</button>}</article>)}</div></section>
      <CheckInResult result={result} onDismiss={dismissResult} />
    </main>
  );
}

function NotFound({ message = 'That page could not be found.' }) {
  return <main className="not-found"><Logo /><p className="eyebrow eyebrow-dark">Ulster Scottish</p><h1>{message}</h1><a href="/">Return to the celebration</a></main>;
}

export default function App() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const route = useMemo(() => window.location.pathname.replace(/\/+$/, '') || '/', []);

  useEffect(() => {
    if (route === '/admin') return;
    api('/api/config').then(setConfig).catch((reason) => setError(reason.message));
  }, [route]);

  if (route === '/admin') return <AdminPage />;
  const orderMatch = route.match(/^\/order\/([^/]+)$/);
  if (orderMatch) return <ReceiptPage token={decodeURIComponent(orderMatch[1])} />;
  const ticketMatch = route.match(/^\/ticket\/([^/]+)$/);
  if (ticketMatch) return <SingleTicketPage token={decodeURIComponent(ticketMatch[1])} />;
  if (route !== '/' && route !== '/option-1' && route !== '/option-2' && route !== '/option-3') return <NotFound />;
  if (error) return <NotFound message={error} />;
  if (!config) return <LoadingPage />;
  if (route === '/option-1') return <OptionOneLanding config={config} />;
  if (route === '/option-2') return <OptionTwoLanding config={config} />;
  if (route === '/option-3') return <OptionThreeLanding config={config} />;
  return <LandingPage config={config} />;
}
